// Assistant Insights (admin dashboard) — shared helpers.
// * tenantTypeOf(user, tenantId) resolves CM vs TBN so callers can pick
//   the right apis.js/tbn_apis.js function without crypto.
// * formatCents / monthKey / computeQuotaPct are cosmetic helpers shared
//   across FaFleetView / FaTenantView / FaUserView.
// * anomalyFlagsForRow / projectEndOfMonthDate encode the cheap rules we
//   run client-side on the landing view.

import {
  get_ai_usage,
  get_ai_usage_history,
  get_ai_usage_users,
  get_ai_usage_fleet,
  get_admin_chat_session,
  get_admin_chat_live_session,
  list_admin_chat_archived_sessions,
  list_admin_chat_archived_sessions_cross_tenant,
  get_admin_chat_live_sessions_cross_tenant,
  get_admin_chat_archived_session,
  get_admin_chat_tools_summary,
  list_fa_documents_company,
} from '../networking/apis'
import {
  tbn_get_ai_usage,
  tbn_get_ai_usage_history,
  tbn_get_ai_usage_users,
  tbn_get_ai_usage_fleet,
  tbn_get_admin_chat_session,
  tbn_get_admin_chat_live_session,
  tbn_list_admin_chat_archived_sessions,
  tbn_list_admin_chat_archived_sessions_cross_tenant,
  tbn_get_admin_chat_live_sessions_cross_tenant,
  tbn_get_admin_chat_archived_session,
  tbn_get_admin_chat_tools_summary,
} from '../networking/tbn_apis'

// admin chats redirect billing to this tenant — see usage_tracker.resolve_billing_tenant_for_current_request.
// when an admin views this tenant's user data in Insights, sessions are
// scattered across the customer tenants they chatted in, not under this id.
export const SUPPORT_BILLING_TENANT_ID = 'support-1df66d7a'

export function isSupportBillingTenant (tenantId) {
  return tenantId === SUPPORT_BILLING_TENANT_ID
}

// resolves to "TBN" or "CM" using the same pattern help_center.vue uses.
export function tenantTypeOf (user, tenantId) {
  if (!user || !user.types) return 'CM'
  return user.types[tenantId] || 'CM'
}

export function isTbnTenant (user, tenantId) {
  return tenantTypeOf(user, tenantId) === 'TBN'
}

// tenant-type-aware api dispatch. each helper returns a promise yielding
// the api response shape already used elsewhere in help_center.vue.
export function apiAiUsage (user, tenantId, period) {
  return (isTbnTenant(user, tenantId) ? tbn_get_ai_usage(tenantId, period) : get_ai_usage(tenantId, period))
}

export function apiAiUsageHistory (user, tenantId, months = 12) {
  return (isTbnTenant(user, tenantId) ? tbn_get_ai_usage_history(tenantId, months) : get_ai_usage_history(tenantId, months))
}

export function apiAiUsageUsers (user, tenantId, period) {
  return (isTbnTenant(user, tenantId) ? tbn_get_ai_usage_users(tenantId, period) : get_ai_usage_users(tenantId, period))
}

// fleet-summary fan-out — splits the tenant list by service, makes one
// batched call per service, merges the rows. one POST replaces N×2 GETs
// when all tenants live on the same service.
export function apiAiUsageFleet (user, period) {
  const opts = (user && user.tenant_options) || []
  const cmTenants = []
  const tbnTenants = []
  opts.forEach(o => {
    const id = typeof o === 'object' ? o.value : o
    if (!id) return
    if (isTbnTenant(user, id)) tbnTenants.push(id)
    else cmTenants.push(id)
  })
  const calls = []
  if (cmTenants.length > 0) calls.push(get_ai_usage_fleet(cmTenants, period))
  if (tbnTenants.length > 0) calls.push(tbn_get_ai_usage_fleet(tbnTenants, period))
  return Promise.allSettled(calls).then(results => {
    const rows = []
    let errored = false
    results.forEach(r => {
      if (r.status !== 'fulfilled') { errored = true; return }
      const payload = ((r.value || {}).value || {}).data || {}
      const data = payload.data || payload
      ;(data.rows || []).forEach(row => rows.push(row))
    })
    return { rows, errored }
  })
}

export function apiAdminChatSession (user, tenantId, sessionId) {
  return (isTbnTenant(user, tenantId) ? tbn_get_admin_chat_session(tenantId, sessionId) : get_admin_chat_session(tenantId, sessionId))
}

export function apiAdminChatLiveSession (user, tenantId, userId) {
  return (isTbnTenant(user, tenantId) ? tbn_get_admin_chat_live_session(tenantId, userId) : get_admin_chat_live_session(tenantId, userId))
}

export function apiListArchivedSessions (user, tenantId, params) {
  return (isTbnTenant(user, tenantId) ? tbn_list_admin_chat_archived_sessions(tenantId, params) : list_admin_chat_archived_sessions(tenantId, params))
}

// fan-out variant: lists archived sessions for one user across every tenant
// the admin can see, splitting the tenant list by service. used by the
// support-tenant user view since admin chats land under each customer tenant.
export function apiListArchivedSessionsCrossTenant (user, userId, limit = 50) {
  const opts = (user && user.tenant_options) || []
  const cmTenants = []
  const tbnTenants = []
  opts.forEach(o => {
    const id = typeof o === 'object' ? o.value : o
    if (!id) return
    if (id === SUPPORT_BILLING_TENANT_ID) return  // don't query the support tenant itself
    if (isTbnTenant(user, id)) tbnTenants.push(id)
    else cmTenants.push(id)
  })
  const calls = []
  if (cmTenants.length > 0) calls.push(list_admin_chat_archived_sessions_cross_tenant(cmTenants, userId, limit))
  if (tbnTenants.length > 0) calls.push(tbn_list_admin_chat_archived_sessions_cross_tenant(tbnTenants, userId, limit))
  return Promise.allSettled(calls).then(results => {
    const merged = []
    results.forEach(r => {
      if (r.status !== 'fulfilled') return
      const payload = ((r.value || {}).value || {}).data || {}
      const data = payload.data || payload
      ;(data.sessions || []).forEach(s => merged.push(s))
    })
    merged.sort((a, b) => Number(b.archived_at || 0) - Number(a.archived_at || 0))
    return { sessions: merged.slice(0, limit) }
  })
}

export function apiAdminChatLiveSessionCrossTenant (user, userId) {
  const opts = (user && user.tenant_options) || []
  const cmTenants = []
  const tbnTenants = []
  opts.forEach(o => {
    const id = typeof o === 'object' ? o.value : o
    if (!id) return
    if (id === SUPPORT_BILLING_TENANT_ID) return
    if (isTbnTenant(user, id)) tbnTenants.push(id)
    else cmTenants.push(id)
  })
  const calls = []
  if (cmTenants.length > 0) calls.push(get_admin_chat_live_sessions_cross_tenant(cmTenants, userId))
  if (tbnTenants.length > 0) calls.push(tbn_get_admin_chat_live_sessions_cross_tenant(tbnTenants, userId))
  return Promise.allSettled(calls).then(results => {
    const merged = []
    results.forEach(r => {
      if (r.status !== 'fulfilled') return
      const payload = ((r.value || {}).value || {}).data || {}
      const data = payload.data || payload
      ;(data.live_sessions || []).forEach(s => merged.push(s))
    })
    merged.sort((a, b) => Number(b.updated_at || 0) - Number(a.updated_at || 0))
    // expose the most-recently-updated one in the existing live-session slot,
    // keep the full list under live_sessions for the UI to surface secondary ones.
    const primary = merged[0] || { exists: false }
    return { ...primary, live_sessions: merged }
  })
}

export function apiGetArchivedSession (user, tenantId, sessionId) {
  return (isTbnTenant(user, tenantId) ? tbn_get_admin_chat_archived_session(tenantId, sessionId) : get_admin_chat_archived_session(tenantId, sessionId))
}

export function apiToolsSummary (user, tenantId, period) {
  return (isTbnTenant(user, tenantId) ? tbn_get_admin_chat_tools_summary(tenantId, period) : get_admin_chat_tools_summary(tenantId, period))
}

export function apiListCompanyDocs (user, tenantId) {
  // no tbn variant yet for fa-documents/company; CM-only for now.
  if (isTbnTenant(user, tenantId)) return Promise.resolve({ value: { data: { documents: [] } } })
  return list_fa_documents_company(tenantId)
}

// --- formatting helpers ---

export function formatCents (cents) {
  const n = Number(cents || 0)
  if (!isFinite(n)) return '$0.00'
  return '$' + (n / 100).toFixed(2)
}

export function formatCentsCompact (cents) {
  const n = Number(cents || 0)
  if (!isFinite(n)) return '$0'
  if (n >= 10000) return '$' + Math.round(n / 100).toLocaleString()
  return '$' + (n / 100).toFixed(2)
}

export function formatTokens (n) {
  const v = Number(n || 0)
  if (!isFinite(v)) return '0'
  if (v >= 1000000) return (v / 1000000).toFixed(1) + 'M'
  if (v >= 1000) return (v / 1000).toFixed(1) + 'k'
  return String(v)
}

export function formatEpoch (epoch) {
  const n = Number(epoch || 0)
  if (!n) return '—'
  try {
    return new Date(n * 1000).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
  } catch (e) {
    return '—'
  }
}

// compact "M/D h:mma" — e.g. 4/24 4:34p. matches the support_ticket_list
// formatDateTimeCompact convention so the FA fleet list and the support
// ticket list share the same dense-cell timestamp shape. for cells where
// vertical space is precious; pair with a `:title="formatEpoch(...)"`
// tooltip so the full timestamp is one hover away.
export function formatEpochCompact (epoch) {
  const n = Number(epoch || 0)
  if (!n) return '—'
  try {
    const d = new Date(n * 1000)
    const m = d.getMonth() + 1
    const day = d.getDate()
    let h = d.getHours()
    const mins = String(d.getMinutes()).padStart(2, '0')
    const ampm = h >= 12 ? 'p' : 'a'
    h = h % 12 || 12
    return m + '/' + day + ' ' + h + ':' + mins + ampm
  } catch (e) {
    return '—'
  }
}

export function monthKey (date) {
  const d = date instanceof Date ? date : new Date()
  const y = d.getFullYear()
  const m = d.getMonth() + 1
  return y + '-' + String(m).padStart(2, '0')
}

// --- period-scoped session filtering ---

// [start, end) epoch-second bounds for a 'YYYY-MM' period, in UTC to match the
// month keys the loaders build and the ai-usage rows are aggregated under.
// null for anything that isn't a well-formed period.
export function periodBounds (period) {
  const m = /^(\d{4})-(\d{2})$/.exec(String(period || ''))
  if (!m) return null
  const year = Number(m[1])
  const monthIdx = Number(m[2]) - 1
  if (monthIdx < 0 || monthIdx > 11) return null
  return {
    start: Date.UTC(year, monthIdx, 1) / 1000,
    end: Date.UTC(year, monthIdx + 1, 1) / 1000,
  }
}

// how many of a session's turns landed inside the period. every turn carries
// its own `timestamp` (written by OpenAIChatService.chat), so whenever the full
// turns array is on the wire this is exact rather than inferred from the
// session's endpoints.
export function turnsInPeriod (turns, period) {
  const b = periodBounds(period)
  if (!b || !Array.isArray(turns)) return 0
  return turns.filter(t => {
    const ts = Number((t && t.timestamp) || 0)
    return ts >= b.start && ts < b.end
  }).length
}

// did this session see activity in the selected month? a conversation has a
// lifespan, not a single date — one opened Jul 30 and still going Aug 2 belongs
// to both months — so this is an overlap test on created_at..updated_at rather
// than a match on any one timestamp. `archived_at` is deliberately not part of
// the span: it records when someone hit "Start New Chat", not when they talked,
// and the usage card above the list is scoped to real activity.
export function sessionOverlapsPeriod (session, period) {
  const b = periodBounds(period)
  if (!b || !session) return false
  // exact path — prefer turn timestamps when we have them, otherwise a session
  // idle for the whole month still passes on the strength of its endpoints.
  // single-tenant live sessions ship full turns; the cross-tenant list ships
  // `turn_timestamps` instead (same information, without the payload).
  if (Array.isArray(session.turns) && session.turns.length > 0) {
    return turnsInPeriod(session.turns, period) > 0
  }
  if (Array.isArray(session.turn_timestamps) && session.turn_timestamps.length > 0) {
    return session.turn_timestamps.some(ts => {
      const n = Number(ts || 0)
      return n >= b.start && n < b.end
    })
  }
  const created = Number(session.created_at || 0)
  const updated = Number(session.updated_at || 0)
  // legacy archive rows predate created_at/updated_at; fall back to archived_at.
  const from = created || updated || Number(session.archived_at || 0)
  if (!from) return false
  const to = Math.max(updated, from)
  return from < b.end && to >= b.start
}

// --- anomaly + projection helpers ---

// simple flags, all surfaced on the fleet landing table. thresholds are
// intentionally conservative so they're actionable, not noisy.
export function anomalyFlagsForRow (row) {
  const flags = []
  const pct = Number(row.usage_percentage) || 0
  if (pct >= 100) flags.push({ key: 'over_limit', label: 'Over limit', severity: 'danger' })
  else if (pct >= 80) flags.push({ key: 'near_limit', label: 'Near limit', severity: 'warning' })
  const mom = Number(row.mom_multiplier) || 0
  if (mom >= 3.0) flags.push({ key: 'mom_spike', label: 'MoM 3×+', severity: 'warning' })
  if (row.fa_enabled && Number(row.chat_requests || 0) === 0 && Number(row.doc_categorizations || 0) === 0) {
    flags.push({ key: 'idle', label: 'No activity', severity: 'muted' })
  }
  return flags
}

// given elapsed month fraction + current spend + monthly limit, return
// the calendar date we'd expect the tenant to hit their limit at current burn.
// null when burn can't be projected (zero elapsed or limit).
export function projectLimitHitDate (usedCents, limitCents, now = new Date()) {
  if (!limitCents || limitCents <= 0 || !usedCents || usedCents <= 0) return null
  const y = now.getFullYear()
  const m = now.getMonth()
  const start = new Date(y, m, 1).getTime()
  const end = new Date(y, m + 1, 1).getTime()
  const elapsedMs = Math.max(1, now.getTime() - start)
  const elapsedFrac = elapsedMs / (end - start)
  if (elapsedFrac <= 0) return null
  const projectedMonth = usedCents / elapsedFrac
  if (projectedMonth < limitCents) return null  // won't hit this month at current pace
  const hitFrac = limitCents / projectedMonth
  const hitMs = start + hitFrac * (end - start)
  return new Date(hitMs)
}

export function computeQuotaPct (usedCents, limitCents) {
  if (!limitCents || limitCents <= 0) return 100
  return Math.max(0, Math.min(100, Math.round((usedCents / limitCents) * 100)))
}

// build the tenant-label map used by the fleet landing table breadcrumb.
export function tenantLabelMap (user) {
  const map = {}
  const opts = (user && user.tenant_options) || []
  opts.forEach(o => {
    const value = typeof o === 'object' ? o.value : o
    const text = typeof o === 'object' ? (o.text || o.value) : o
    map[value] = text
  })
  return map
}
